import { Badge, Icon } from '@tf/ui';
import { fmt } from '@tf/utils';
import type React from 'react';

import type {
	Decision,
	OrderType,
	PaymentDecisionStatus,
	PaymentStatus,
	TransactionAssessment,
} from '@/core/types/payments';

export const orderTypesMap: Record<OrderType, string> = {
	PAYMENT_IN: 'Third-party',
	PAYMENT_OUT: 'Third-party',
	CASH_IN: 'First-party',
	CASH_OUT: 'First-party',
};

export const extractAssessmentData = (assessment: TransactionAssessment) => {
	if (assessment.data.result) {
		const {
			actions,
			messages,
			manual_review_needed: manualReviewNeeded,
			risk_level,
			risk_score,
		} = assessment.data.result;
		return { actions, messages, manualReviewNeeded, riskLevel: risk_level, riskScore: risk_score };
	}
	if (assessment.data.error) {
		const { message: errorMessage, details: errorDetails } = assessment.data.error;
		return { errorMessage, errorDetails };
	}
	return {};
};

export const getRiskBadge = (level: string, label?: string | number) => {
	const variants = {
		LOW: { color: 'green', bg: '#d3f9d8' },
		MEDIUM: { color: '#f59f00', bg: '#fff3bf' },
		HIGH: { color: 'red', bg: '#ffe3e3' },
		VERY_HIGH: { color: 'red', bg: '#ffe3e3' },
	} as const;

	const { color, bg } = variants[level as 'LOW' | 'MEDIUM' | 'HIGH'] || { color: 'gray', bg: '#f1f3f5' };

	return (
		<Badge mr="8px" c={color} bg={bg} variant="light">
			{label || level.toLowerCase()}
		</Badge>
	);
};

export const getDecisionStatus = (status: PaymentStatus): PaymentDecisionStatus | undefined => {
	if (status === 'APPROVED' || status === 'ORDER_COMPLETED' || status === 'APPROVED_ERROR') {
		return 'APPROVED';
	}
	if (status === 'REJECTED' || status === 'ORDER_REJECTED' || status === 'REJECTED_ERROR') {
		return 'REJECTED';
	}
};

export const isPaymentInErrorState = (status: PaymentStatus): boolean => {
	return status === 'APPROVED_ERROR' || status === 'REJECTED_ERROR';
};

export const getOriginalStatusFromError = (status: PaymentStatus): PaymentDecisionStatus | undefined => {
	if (status === 'APPROVED_ERROR') return 'APPROVED';
	if (status === 'REJECTED_ERROR') return 'REJECTED';
	return undefined;
};

export const shouldShowResendButton = (status: PaymentStatus): boolean => {
	return isPaymentInErrorState(status);
};

export const getAssessmentAlertConfig = (
	riskMessage: RiskMessage,
	decisionStatus?: PaymentDecisionStatus,
	errorMessage?: string,
	manualReviewNeeded?: boolean,
	riskLevel?: string,
) => {
	// The APPROVED branch below would claim low risk
	if (riskLevel === NOT_ASSESSED && !errorMessage) {
		return {
			color: '64, 192, 87',
			icon: <Icon.IconCircleCheckFilled />,
			title: riskMessage.label,
		};
	}

	if (manualReviewNeeded || errorMessage) {
		return {
			color: riskMessage.baseColor,
			icon: riskMessage.icon,
			title: riskMessage.label,
		};
	}

	if (decisionStatus === 'APPROVED') {
		return {
			color: '64, 192, 87',
			icon: <Icon.IconCircleCheckFilled />,
			title: 'The payment was assessed with low risk and approved automatically',
		};
	}

	if (decisionStatus === 'REJECTED') {
		return {
			color: '255, 107, 107',
			icon: <Icon.IconCircleX />,
			title: riskMessage.label,
		};
	}

	return {
		color: '211, 211, 211',
		icon: <Icon.IconInfoCircle />,
		title: 'Unknown assessment status',
	};
};

enum RiskLevel {
	LOW = 'LOW',
	MEDIUM = 'MEDIUM',
	HIGH = 'HIGH',
}

const NOT_ASSESSED = 'NOT_ASSESSED';

type RiskMessage = {
	label: string;
	description: string;
	baseColor: string;
	icon: React.ReactNode;
};

export const getRiskLevelMessage = (
	assessment: TransactionAssessment,
	decisionStatus?: PaymentDecisionStatus,
	decision?: Decision,
) => {
	const { riskLevel, errorMessage } = extractAssessmentData(assessment);

	if (errorMessage) {
		return {
			label: 'Assessment error',
			description: errorMessage,
			baseColor: '255, 107, 107',
			icon: <Icon.IconExclamationCircle />,
		};
	}

	if (decision && decisionStatus) {
		const { comment, made_at } = decision;

		switch (riskLevel) {
			case RiskLevel.MEDIUM:
				return {
					label: `The payment was assessed ${riskLevel} risk and ${decisionStatus} with the comment: ${comment}`,
					description: `Decision is made ${fmt.toDate(made_at, { preset: 'full_date_with_time' })}`,
					baseColor: '246, 102, 6',
					icon: <Icon.IconFlagFilled />,
				};
			case RiskLevel.HIGH:
				return {
					label: `The payment was assessed ${riskLevel} risk and ${decisionStatus} with the comment: ${comment}`,
					description: `Decision is made ${fmt.toDate(made_at, { preset: 'full_date_with_time' })}`,
					baseColor: '255, 107, 107',
					icon: <Icon.IconFlagFilled />,
				};
		}
	}

	if (riskLevel === NOT_ASSESSED) {
		return {
			label:
				decisionStatus === 'REJECTED'
					? 'No risk assessment applied. The payment was rejected'
					: 'No risk assessment applied. The payment was approved automatically',
			description: '',
			baseColor: decisionStatus === 'REJECTED' ? '255, 107, 107' : '64, 192, 87',
			icon: <Icon.IconCircleCheckFilled />,
		};
	}

	const createdAt = fmt.toDate(assessment.meta['@createdAt'], { preset: 'full_date_with_time' });
	switch (riskLevel) {
		case RiskLevel.LOW:
			return {
				label: 'The payment was assessed with LOW risk and APPROVED automatically',
				description: `Based on the assessment results from  ${createdAt}`,
				baseColor: '64, 192, 87',
				icon: <Icon.IconCircleCheckFilled />,
			};
		case RiskLevel.MEDIUM:
			return {
				label: 'The payment was assessed with MEDIUM risk and flagged for additional review',
				description: `Based on the assessment results from  ${createdAt}`,
				baseColor: '246, 102, 6',
				icon: <Icon.IconFlagFilled />,
			};
		case RiskLevel.HIGH:
			return {
				label: 'The payment was assessed with HIGH risk and flagged for additional review',
				description: `Based on the assessment results from  ${createdAt}`,
				baseColor: '255, 107, 107',
				icon: <Icon.IconFlagFilled />,
			};

		default:
			return {
				label: 'Assessment error',
				description: `Something went wrong`,
				baseColor: '255, 107, 107',
				icon: <Icon.IconExclamationCircle />,
			};
	}
};
